import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Sparkle, Drop, Pencil } from '@phosphor-icons/react';
import DetailingLandingEditor from './DetailingLandingEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DetailingTab() {
  const { t } = useLanguage();
  const [enabled, setEnabled] = useState(false);
  const [detPloteEnabled, setDetPloteEnabled] = useState(false);
  const [services, setServices] = useState([]);
  const [view, setView] = useState('services');  // services | landing_editor
  // Iter24 G — premium currency (EUR | LEK) + rate
  const [premiumCurrency, setPremiumCurrency] = useState('EUR');
  const [eurToLekRate, setEurToLekRate] = useState(98);

  const fetchAll = async () => {
    try {
      const [s, srv, pc] = await Promise.all([
        axios.get(`${API}/settings/public`),
        axios.get(`${API}/detailing/services`, { withCredentials: true }),
        axios.get(`${API}/business-settings/premium-currency`, { withCredentials: true })
              .catch(() => ({ data: { premium_currency: 'EUR', eur_to_lek_rate: 98 } })),
      ]);
      setEnabled(!!s.data?.detailing_enabled);
      setDetPloteEnabled(!!s.data?.detailing_i_plote_enabled);
      setServices(srv.data || []);
      setPremiumCurrency(pc.data?.premium_currency || 'EUR');
      setEurToLekRate(pc.data?.eur_to_lek_rate || 98);
    } catch (e) { toast.error('Failed to load'); }
  };
  useEffect(() => { fetchAll(); }, []);

  // Iter24 G — save currency choice (EUR | LEK) + EUR→LEK rate.
  const savePremiumCurrency = async (nextCur, nextRate) => {
    try {
      const payload = {
        premium_currency: nextCur ?? premiumCurrency,
        eur_to_lek_rate: nextRate ?? eurToLekRate,
      };
      await axios.put(`${API}/business-settings/premium-currency`, payload, { withCredentials: true });
      setPremiumCurrency(payload.premium_currency);
      setEurToLekRate(payload.eur_to_lek_rate);
      toast.success('OK');
    } catch { toast.error('Error'); }
  };

  const toggleEnabled = async () => {
    const next = !enabled;
    try {
      await axios.put(`${API}/business-settings/detailing`, { enabled: next }, { withCredentials: true });
      setEnabled(next);
      toast.success(next ? 'ON' : 'OFF');
    } catch (e) { toast.error('Error'); }
  };

  const toggleDetPlote = async () => {
    const next = !detPloteEnabled;
    try {
      await axios.put(`${API}/business-settings/detailing-i-plote`, { enabled: next }, { withCredentials: true });
      setDetPloteEnabled(next);
      toast.success(next ? 'ON' : 'OFF');
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const saveServiceField = async (svc, patch) => {
    try {
      await axios.put(`${API}/detailing/services/${svc.service_id}`, patch, { withCredentials: true });
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const liquidGlassSvcs = services.filter((s) => s.category !== 'detailing_i_plote');
  const detPloteSvcs = services.filter((s) => s.category === 'detailing_i_plote');

  return (
    <div className="space-y-4" data-testid="detailing-tab">
      {/* View switcher */}
      <div className="flex gap-2 p-1 bg-[#0a0a0a] border border-white/5 rounded-2xl">
        <button onClick={() => setView('services')} data-testid="detailing-view-services"
                className={`flex-1 h-9 rounded-xl text-xs font-semibold ${view === 'services' ? 'bg-[#0ABAB5] text-black' : 'text-white/55'}`}>
          Shërbimet
        </button>
        <button onClick={() => setView('landing_editor')} data-testid="detailing-view-landing"
                className={`flex-1 h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1 ${view === 'landing_editor' ? 'bg-[#0ABAB5] text-black' : 'text-white/55'}`}>
          <Pencil size={12} weight="fill" /> Faqja Detailing
        </button>
      </div>

      {view === 'landing_editor' ? (
        <DetailingLandingEditor />
      ) : (
        <>
          {/* Master toggle — Liquid Glass */}
          <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FF5A00]/10 to-[#0ABAB5]/10 border border-[#FF5A00]/20">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkle size={18} weight="fill" className="text-[#FF5A00]" />
                  <p className="text-sm font-bold text-white">Aktivizo Liquid Glass</p>
                </div>
                <p className="text-xs text-white/45 leading-relaxed">{t('detailingMasterHelp')}</p>
              </div>
              <button onClick={toggleEnabled} data-testid="detailing-master-toggle"
                      className={`relative w-12 h-7 rounded-full shrink-0 transition ${enabled ? 'bg-[#0ABAB5]' : 'bg-white/15'}`}>
                <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition ${enabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
          </div>

          {/* Iter23 E2 — Master toggle Detailing i Plotë (independent) */}
          <div className="p-4 rounded-2xl bg-gradient-to-br from-[#A78BFA]/10 to-[#0ABAB5]/10 border border-[#A78BFA]/20">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Drop size={18} weight="fill" className="text-[#A78BFA]" />
                  <p className="text-sm font-bold text-white">Aktivizo Detailing i Plotë</p>
                </div>
                <p className="text-xs text-white/45 leading-relaxed">
                  Pastrim i plotë (brendësi, jashtësi, motor) me produkte AutoSpa5.
                </p>
              </div>
              <button onClick={toggleDetPlote} data-testid="detailing-i-plote-toggle"
                      className={`relative w-12 h-7 rounded-full shrink-0 transition ${detPloteEnabled ? 'bg-[#A78BFA]' : 'bg-white/15'}`}>
                <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition ${detPloteEnabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
          </div>

          {/* Iter28 FIX 3 — Tire-shine card removed. Shkëlqim gomash is now PERMANENT
              on Eco+/Std/Lux (rendered as a chip on those program cards in /programs). */}

          {/* Iter24 G — Premium currency toggle EUR ↔ LEK + EUR→LEK rate */}
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/10" data-testid="premium-currency-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-bold text-white">Monedha e shfaqur</p>
                <p className="text-[10px] text-white/45 leading-relaxed">Si shfaqen çmimet Premium në app: EUR ose LEK.</p>
              </div>
              <div className="flex gap-1 p-1 bg-[#050505] border border-white/10 rounded-xl" data-testid="premium-currency-toggle">
                <button onClick={() => savePremiumCurrency('EUR', null)}
                        data-testid="premium-currency-eur"
                        className={`px-3 h-7 rounded-lg text-xs font-bold ${premiumCurrency === 'EUR' ? 'bg-[#0ABAB5] text-black' : 'text-white/55'}`}>
                  €
                </button>
                <button onClick={() => savePremiumCurrency('LEK', null)}
                        data-testid="premium-currency-lek"
                        className={`px-3 h-7 rounded-lg text-xs font-bold ${premiumCurrency === 'LEK' ? 'bg-[#0ABAB5] text-black' : 'text-white/55'}`}>
                  L
                </button>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider">Kursi (1 € = ? L)</label>
              <Input type="number" defaultValue={eurToLekRate} step="0.1" min="1"
                     onBlur={(e) => {
                       const v = parseFloat(e.target.value);
                       if (!Number.isNaN(v) && v !== eurToLekRate) savePremiumCurrency(null, v);
                     }}
                     className="h-8 bg-[#050505] border-white/10 rounded-lg text-sm text-right"
                     data-testid="premium-currency-rate-input" />
            </div>
          </div>

          {/* Liquid Glass services */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-white/55 uppercase tracking-wider">Liquid Glass</h4>
            {liquidGlassSvcs.length === 0 ? (
              <p className="text-center text-white/35 text-sm py-4">—</p>
            ) : liquidGlassSvcs.map((svc) => (
              <ServiceCard key={svc.service_id} svc={svc} t={t} onSave={saveServiceField} />
            ))}
          </div>

          {/* Detailing i Plotë services */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-white/55 uppercase tracking-wider">Detailing i Plotë</h4>
            {detPloteSvcs.length === 0 ? (
              <p className="text-center text-white/35 text-sm py-4">—</p>
            ) : detPloteSvcs.map((svc) => (
              <ServiceCard key={svc.service_id} svc={svc} t={t} onSave={saveServiceField} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ServiceCard({ svc, t, onSave }) {
  return (
    <div className={`p-3 rounded-2xl border ${svc.active ? 'bg-[#0a0a0a] border-white/5' : 'opacity-50 border-white/5'}`}
         data-testid={`detailing-svc-${svc.code}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-white truncate">{svc.name}</p>
        <button onClick={() => onSave(svc, { active: !svc.active })}
                className={`text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider ${
                  svc.active ? 'bg-[#0ABAB5]/15 text-[#0ABAB5]' : 'bg-white/5 text-white/40'
                }`}
                data-testid={`detailing-svc-${svc.code}-active`}>
          {svc.active ? 'aktiv' : 'jo aktiv'}
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] text-white/40 uppercase">€ Çmimi</label>
          <Input type="number" defaultValue={svc.price_eur}
                 onBlur={(e) => {
                   const v = parseFloat(e.target.value);
                   if (!Number.isNaN(v) && v !== svc.price_eur) onSave(svc, { price_eur: v });
                 }}
                 className="h-8 bg-[#050505] border-white/10 rounded-lg text-sm text-right"
                 data-testid={`detailing-svc-${svc.code}-price`} />
        </div>
        <div>
          <label className="text-[10px] text-white/40 uppercase">€ Origjinal</label>
          <Input type="number" defaultValue={svc.original_price_eur || 0}
                 onBlur={(e) => {
                   const v = parseFloat(e.target.value);
                   if (!Number.isNaN(v) && v !== (svc.original_price_eur || 0)) onSave(svc, { original_price_eur: v });
                 }}
                 className="h-8 bg-[#050505] border-white/10 rounded-lg text-sm text-right"
                 data-testid={`detailing-svc-${svc.code}-original-price`} />
        </div>
        <div>
          <label className="text-[10px] text-white/40 uppercase">Min</label>
          <Input type="number" defaultValue={svc.duration_minutes}
                 onBlur={(e) => {
                   const v = parseInt(e.target.value);
                   if (!Number.isNaN(v) && v !== svc.duration_minutes) onSave(svc, { duration_minutes: v });
                 }}
                 className="h-8 bg-[#050505] border-white/10 rounded-lg text-sm text-right" />
        </div>
        <div>
          <label className="text-[10px] text-white/40 uppercase">€ Kosto</label>
          <Input type="number" defaultValue={svc.product_cost_eur ?? 27}
                 onBlur={(e) => {
                   const v = parseFloat(e.target.value);
                   if (!Number.isNaN(v) && v !== svc.product_cost_eur) onSave(svc, { product_cost_eur: v });
                 }}
                 className="h-8 bg-[#050505] border-white/10 rounded-lg text-sm text-right" />
        </div>
      </div>
    </div>
  );
}
